import React, { useState } from 'react';
import { Calendar, Clock, Users, Video, Phone, Monitor, Repeat, Trash2, Play, Copy, Check, MapPin, Mail, Download, Share2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Meeting, meetingService } from '../../../services/meeting.service.ts';
import { validateMeetingLinkAccess } from '../../meeting/utils/meetingLinkExpiry.ts';
import { isMeetingLiveActive } from '../../meeting/utils/meetingLiveExpiry.ts';
import { authService } from '../../../services/auth.service.ts';
import { getUserPlan, resolveRoomDurationLimitSeconds } from '../../../utils/plan.ts';
import {
  createRoomTimePolicy,
  writeRoomTimePolicy,
} from '../../meeting/utils/roomTimePolicy.ts';

interface MeetingCardProps {
  meeting: Meeting;
  onDelete: (id: string) => void;
}

const MeetingCard: React.FC<MeetingCardProps> = ({ meeting, onDelete }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [showInvitation, setShowInvitation] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('tr-TR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatShortDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('tr-TR', { month: 'short' }).toUpperCase();
    const year = date.getFullYear();
    return { day, month, year };
  };

  const getMeetingTypeIcon = () => {
    if (meeting.meetingFormat === 'IN_PERSON') {
      return <MapPin className="w-4 h-4" />;
    }
    switch (meeting.meetingType) {
      case 'VIDEO': return <Video className="w-4 h-4" />;
      case 'AUDIO': return <Phone className="w-4 h-4" />;
      case 'SCREEN': return <Monitor className="w-4 h-4" />;
      default: return <Video className="w-4 h-4" />;
    }
  };

  const getMeetingTypeLabel = () => {
    if (meeting.meetingFormat === 'IN_PERSON') return 'Yüz Yüze';
    switch (meeting.meetingType) {
      case 'VIDEO': return 'Görüntülü';
      case 'AUDIO': return 'Sesli';
      case 'SCREEN': return 'Ekran Paylaşımı';
      default: return 'Görüntülü';
    }
  };

  const handleJoinMeeting = async () => {
    if (meeting.meetingFormat !== 'IN_PERSON' && meeting.isInstant) {
      const result = await validateMeetingLinkAccess(meeting.meetingCode);
      if (!result.allowed && result.expired) {
        window.alert('Bu toplantı linkinin süresi dolmuş (1 saat).');
        return;
      }
    }

    if (
      meeting.meetingFormat !== 'IN_PERSON' &&
      !meeting.isInstant &&
      meeting.status === 'SCHEDULED'
    ) {
      try {
        await meetingService.startMeeting(meeting.id);
      } catch (error) {
        console.error('Toplantı başlatılamadı:', error);
        return;
      }
    }

    if (meeting.meetingFormat !== 'IN_PERSON' && !isMeetingLiveActive(meeting)) {
      const recheck = await validateMeetingLinkAccess(meeting.meetingCode);
      if (!recheck.allowed && recheck.expired) {
        window.alert('Bu toplantının süresi dolmuş (50 dakika).');
        return;
      }
    }

    if (meeting.meetingFormat !== 'IN_PERSON') {
      const limitSeconds = resolveRoomDurationLimitSeconds(
        getUserPlan(authService.getUser()),
        meeting.duration
      );
      if (limitSeconds != null) {
        writeRoomTimePolicy(
          meeting.meetingCode,
          createRoomTimePolicy(limitSeconds, Date.now())
        );
      }
    }

    navigate(`/meeting/${meeting.meetingCode}`);
  };
  
  const handleCopyCode = async () => {
    await navigator.clipboard.writeText(meeting.meetingCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDelete = () => {
    if (window.confirm('Bu toplantıyı silmek istediğinize emin misiniz?')) {
      onDelete(meeting.id);
    }
  };

  const generateInvitationImage = async (): Promise<string> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    
    // Yatay kart boyutu (16:9 landscape)
    canvas.width = 1200;
    canvas.height = 675;

    // Gradient arka plan
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, '#1e1b4b');
    gradient.addColorStop(0.5, '#312e81');
    gradient.addColorStop(1, '#581c87');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Dekoratif daireler
    ctx.beginPath();
    ctx.arc(-50, 100, 200, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(168, 85, 247, 0.15)';
    ctx.fill();

    ctx.beginPath();
    ctx.arc(1250, 600, 250, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(236, 72, 153, 0.15)';
    ctx.fill();

    // Sol taraf - Tarih bloğu
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.beginPath();
    ctx.roundRect(50, 50, 200, 575, 20);
    ctx.fill();

    const dateInfo = formatShortDate(meeting.scheduledAt || '');
    
    // Ay
    ctx.fillStyle = '#a855f7';
    ctx.font = 'bold 28px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText(dateInfo.month, 150, 120);

    // Gün
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 80px system-ui';
    ctx.fillText(dateInfo.day.toString(), 150, 210);

    // Yıl
    ctx.fillStyle = '#a1a1aa';
    ctx.font = '24px system-ui';
    ctx.fillText(dateInfo.year.toString(), 150, 250);

    // Saat
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 36px system-ui';
    const time = formatTime(meeting.scheduledAt || '');
    ctx.fillText(time, 150, 340);

    // Süre
    ctx.fillStyle = '#a1a1aa';
    ctx.font = '20px system-ui';
    ctx.fillText(`${meeting.duration} dakika`, 150, 380);

    // Konum ikonu
    ctx.fillStyle = '#22c55e';
    ctx.beginPath();
    ctx.arc(150, 480, 35, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.fillStyle = '#ffffff';
    ctx.font = '30px system-ui';
    ctx.fillText('📍', 150, 490);

    // Yüz yüze etiketi
    ctx.fillStyle = '#22c55e';
    ctx.font = 'bold 16px system-ui';
    ctx.fillText('YÜZ YÜZE', 150, 550);

    // Sağ taraf - İçerik
    // Başlık
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 42px system-ui';
    ctx.textAlign = 'left';
    
    let title = meeting.title;
    if (ctx.measureText(title).width > 650) {
      title = title.substring(0, 30) + '...';
    }
    ctx.fillText(title, 300, 120);

    // Açıklama
    if (meeting.description) {
      ctx.fillStyle = '#a1a1aa';
      ctx.font = '22px system-ui';
      let desc = meeting.description;
      if (desc.length > 60) desc = desc.substring(0, 60) + '...';
      ctx.fillText(desc, 300, 165);
    }

    // Ayırıcı çizgi
    ctx.beginPath();
    ctx.moveTo(300, 200);
    ctx.lineTo(1100, 200);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Mekan bilgileri
    let yPos = 260;

    if (meeting.location) {
      ctx.fillStyle = '#22c55e';
      ctx.font = '18px system-ui';
      ctx.fillText('MEKAN', 300, yPos);
      
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 28px system-ui';
      ctx.fillText(meeting.location, 300, yPos + 35);
      yPos += 90;
    }

    if (meeting.building || meeting.room) {
      ctx.fillStyle = '#a855f7';
      ctx.font = '18px system-ui';
      ctx.fillText('BİNA / ODA', 300, yPos);
      
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 24px system-ui';
      const buildingRoom = [meeting.building, meeting.room].filter(Boolean).join(' • ');
      ctx.fillText(buildingRoom, 300, yPos + 35);
      yPos += 90;
    }

    if (meeting.address) {
      ctx.fillStyle = '#6b7280';
      ctx.font = '18px system-ui';
      ctx.fillText('ADRES', 300, yPos);
      
      ctx.fillStyle = '#d1d5db';
      ctx.font = '20px system-ui';
      let addr = meeting.address;
      if (addr.length > 50) addr = addr.substring(0, 50) + '...';
      ctx.fillText(addr, 300, yPos + 30);
      yPos += 70;
    }

    // Alt kısım - Katılımcı ve branding
    ctx.beginPath();
    ctx.moveTo(300, 550);
    ctx.lineTo(1100, 550);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Katılımcı
    if (meeting.participants && meeting.participants.length > 0) {
      ctx.fillStyle = '#a1a1aa';
      ctx.font = '18px system-ui';
      ctx.fillText(`👥 ${meeting.participants.length} katılımcı davet edildi`, 300, 600);
    }

    // TBKMeet branding
    ctx.fillStyle = '#6b7280';
    ctx.font = '16px system-ui';
    ctx.textAlign = 'right';
    ctx.fillText('TBKMeet ile oluşturuldu', 1100, 600);

    // Davetiye başlığı (sağ üst)
    ctx.fillStyle = 'rgba(168, 85, 247, 0.3)';
    ctx.beginPath();
    ctx.roundRect(900, 40, 200, 40, 20);
    ctx.fill();

    ctx.fillStyle = '#c4b5fd';
    ctx.font = 'bold 14px system-ui';
    ctx.textAlign = 'center';
    ctx.fillText('TOPLANTI DAVETİYESİ', 1000, 67);

    return canvas.toDataURL('image/png');
  };

  const handleDownloadInvitation = async () => {
    setDownloading(true);
    try {
      const imageData = await generateInvitationImage();
      const link = document.createElement('a');
      link.download = `davetiye-${meeting.meetingCode}.png`;
      link.href = imageData;
      link.click();
    } catch (error) {
      console.error('Davetiye oluşturulamadı:', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleShareInvitation = async () => {
    const shareText = `📌 ${meeting.title}

📅 ${formatDate(meeting.scheduledAt || '')}
🕐 ${formatTime(meeting.scheduledAt || '')} (${meeting.duration} dk)

📍 ${meeting.location || ''}
${meeting.building ? `🏢 ${meeting.building}` : ''}${meeting.room ? ` • ${meeting.room}` : ''}
${meeting.address ? `📫 ${meeting.address}` : ''}

Katılımınızı bekliyoruz!`;

    if (navigator.share) {
      try {
        await navigator.share({ title: `Davetiye: ${meeting.title}`, text: shareText });
      } catch (err) {}
    } else {
      await navigator.clipboard.writeText(shareText);
      alert('Davetiye metni panoya kopyalandı!');
    }
  };

  const isPastMeeting = meeting.scheduledAt ? new Date(meeting.scheduledAt) < new Date() : false;
  const isLive = isMeetingLiveActive(meeting);
  const isInPerson = meeting.meetingFormat === 'IN_PERSON';
  const dateInfo = formatShortDate(meeting.scheduledAt || '');

  return (
    <>
      <div className={`bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4 sm:p-6 transition hover:border-purple-500/50 ${isPastMeeting && !isLive ? 'opacity-60' : ''}`}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h3 className="text-base sm:text-xl font-semibold text-white break-words">{meeting.title}</h3>
              {isLive && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-600/20 text-red-300 rounded text-xs shrink-0 animate-pulse">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  <span>Canlı</span>
                </span>
              )}
              {isInPerson && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-600/20 text-green-300 rounded text-xs shrink-0">
                  <MapPin className="w-3 h-3" />
                  <span>Yüz Yüze</span>
                </span>
              )}
              {meeting.isRecurring && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-600/20 text-purple-300 rounded text-xs shrink-0">
                  <Repeat className="w-3 h-3" />
                  <span>{meeting.recurrenceType === 'WEEKLY' ? 'Haftalık' : meeting.recurrenceType === 'DAILY' ? 'Günlük' : 'Aylık'}</span>
                </span>
              )}
            </div>
            {meeting.description && (
              <p className="text-gray-400 text-sm mb-3 line-clamp-2">{meeting.description}</p>
            )}
            
            {isInPerson && meeting.location && (
              <div className="flex items-start gap-2 text-sm text-gray-300 mb-2 bg-white/5 rounded-lg p-2">
                <MapPin className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium">{meeting.location}</span>
                  {(meeting.building || meeting.room) && (
                    <span className="text-gray-400"> • {[meeting.building, meeting.room].filter(Boolean).join(', ')}</span>
                  )}
                </div>
              </div>
            )}
            
            {!isInPerson && (
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span className="font-mono bg-white/5 px-2 py-1 rounded">{meeting.meetingCode}</span>
                <button onClick={handleCopyCode} className="p-1 hover:text-white transition">
                  {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            )}
          </div>
          <button
            onClick={handleDelete}
            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4">
          {meeting.scheduledAt && (
            <>
              <div className="flex items-center gap-2 text-gray-300">
                <Calendar className="w-4 h-4 text-purple-400" />
                <span className="text-sm truncate">{formatDate(meeting.scheduledAt)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <Clock className="w-4 h-4 text-purple-400" />
                <span className="text-sm">{formatTime(meeting.scheduledAt)} – {meeting.duration} dk</span>
              </div>
            </>
          )}
          <div className="flex items-center gap-2 text-gray-300">
            {getMeetingTypeIcon()}
            <span className="text-sm">{getMeetingTypeLabel()}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-300">
            <Users className="w-4 h-4 text-purple-400" />
            <span className="text-sm">{meeting.participants?.length || 0} katılımcı</span>
          </div>
        </div>

        <div className="flex justify-start pt-4 border-t border-white/10">
          {isInPerson ? (
            <button
              onClick={() => setShowInvitation(true)}
              className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white text-sm rounded-lg font-medium transition active:scale-95 flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4" />
              <span>Davetiye Oluştur</span>
            </button>
          ) : (
            <button
              onClick={handleJoinMeeting}
              className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-sm rounded-lg font-medium transition active:scale-95 flex items-center justify-center gap-2"
            >
              <Play className="w-4 h-4" />
              <span>{isLive ? 'Toplantıya Katıl' : 'Toplantıyı Başlat'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Yatay Davetiye Modalı */}
      {showInvitation && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-2xl border border-white/10 w-full max-w-3xl shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-lg font-bold text-white">Toplantı Davetiyesi</h3>
              <button
                onClick={() => setShowInvitation(false)}
                className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Yatay Davetiye Kartı (mobilde dikey stack, sm+ yatay) */}
            <div className="p-4">
              <div className="bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900 rounded-xl overflow-hidden">
                <div className="flex flex-col sm:flex-row">
                  {/* Sol/Üst - Tarih Bloğu */}
                  <div className="w-full sm:w-40 bg-white/5 flex flex-row sm:flex-col items-center justify-around sm:justify-center gap-3 sm:gap-0 p-3 sm:p-4 border-b sm:border-b-0 sm:border-r border-white/10">
                    <div className="flex flex-col items-center">
                      <span className="text-purple-400 font-bold text-sm">{dateInfo.month}</span>
                      <span className="text-white font-bold text-4xl sm:text-6xl leading-none">{dateInfo.day}</span>
                      <span className="text-gray-400 text-sm">{dateInfo.year}</span>
                    </div>
                    <div className="text-center sm:mt-4 sm:pt-4 sm:border-t sm:border-white/10">
                      <span className="text-white font-bold text-lg sm:text-xl">{formatTime(meeting.scheduledAt || '')}</span>
                      <p className="text-gray-400 text-xs mt-1">{meeting.duration} dakika</p>
                    </div>
                    <div className="flex flex-col items-center sm:mt-4 sm:pt-4 sm:border-t sm:border-white/10">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>
                      <p className="text-green-400 text-[10px] sm:text-xs font-bold mt-1 sm:mt-2">YÜZ YÜZE</p>
                    </div>
                  </div>

                  {/* Sağ/Alt - İçerik */}
                  <div className="flex-1 p-4 sm:p-5 min-w-0">
                    {/* Üst badge */}
                    <div className="flex justify-end mb-2">
                      <span className="px-3 py-1 bg-purple-500/30 text-purple-300 text-xs font-bold rounded-full">
                        TOPLANTI DAVETİYESİ
                      </span>
                    </div>

                    {/* Başlık */}
                    <h4 className="text-xl sm:text-3xl font-bold text-white mb-1 break-words">{meeting.title}</h4>
                    {meeting.description && (
                      <p className="text-gray-400 text-sm mb-4 line-clamp-2 sm:line-clamp-1 break-words">{meeting.description}</p>
                    )}

                    {/* Mekan bilgileri */}
                    <div className="space-y-3 mt-4 pt-4 border-t border-white/10">
                      {meeting.location && (
                        <div>
                          <p className="text-green-400 text-xs font-bold uppercase">Mekan</p>
                          <p className="text-white font-semibold">{meeting.location}</p>
                        </div>
                      )}
                      
                      {(meeting.building || meeting.room) && (
                        <div>
                          <p className="text-purple-400 text-xs font-bold uppercase">Bina / Oda</p>
                          <p className="text-white">{[meeting.building, meeting.room].filter(Boolean).join(' • ')}</p>
                        </div>
                      )}

                      {meeting.address && (
                        <div>
                          <p className="text-gray-500 text-xs font-bold uppercase">Adres</p>
                          <p className="text-gray-300 text-sm">{meeting.address}</p>
                        </div>
                      )}
                    </div>

                    {/* Alt kısım */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                      {meeting.participants && meeting.participants.length > 0 && (
                        <span className="text-gray-400 text-sm">
                          👥 {meeting.participants.length} katılımcı
                        </span>
                      )}
                      <span className="text-gray-500 text-xs">TBKMeet ile oluşturuldu</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Butonlar */}
            <div className="flex gap-3 p-4 border-t border-white/10">
              <button
                onClick={handleDownloadInvitation}
                disabled={downloading}
                className="flex-1 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl font-medium transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {downloading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>PNG İndir</span>
              </button>
              <button
                onClick={handleShareInvitation}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium transition flex items-center justify-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                <span>Paylaş</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MeetingCard;